// Loads translated entries from translation_queue and streams updates into i18n.
// - On mount: fetch all translated rows once and merge into all language bundles.
// - Realtime: subscribe to INSERT/UPDATE on translation_queue and merge live.

import { useEffect } from "react";
import i18n from "@/lib/i18n";
import { supabase } from "@/integrations/supabase/client";

const SUPPORTED = ["pt", "en", "fr", "zh"] as const;

type Row = {
  translation_key: string;
  default_text: string;
  translations: Record<string, string> | null;
};

function applyRow(row: Row) {
  // Always seed PT with the canonical default so tAuto works without round-trips.
  i18n.addResource("pt", "translation", row.translation_key, row.default_text);
  const t = row.translations ?? {};
  for (const lng of SUPPORTED) {
    if (lng === "pt") continue;
    const val = t[lng];
    if (typeof val === "string" && val.length > 0) {
      i18n.addResource(lng, "translation", row.translation_key, val);
    }
  }
}

let bootstrapped = false;

export function useDynamicTranslations() {
  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      if (bootstrapped) return;
      bootstrapped = true;
      try {
        // Paginate (PostgREST hard cap 1000 by default).
        let from = 0;
        const PAGE = 1000;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { data, error } = await (supabase as any)
            .from("translation_queue")
            .select("translation_key, default_text, translations")
            .order("created_at", { ascending: true })
            .range(from, from + PAGE - 1);
          if (error) {
            console.warn("[i18n] bootstrap fetch failed", error);
            return;
          }
          if (!data?.length) break;
          for (const row of data as Row[]) {
            if (cancelled) return;
            applyRow(row);
          }
          if (data.length < PAGE) break;
          from += PAGE;
        }
        // Force re-render of components using useTranslation.
        i18n.emit("languageChanged", i18n.language);
      } catch (e) {
        console.warn("[i18n] bootstrap error", e);
      }
    }

    bootstrap();

    const channel = supabase
      .channel("translation_queue_stream")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "translation_queue" },
        (payload: any) => {
          const row = (payload.new ?? payload.record) as Row | undefined;
          if (!row) return;
          applyRow(row);
          i18n.emit("languageChanged", i18n.language);
        },
      )
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, []);
}
